import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  code: "", description: "", discount_type: "percent", discount_value: 10, active: true,
  expires_at: "", usage_limit: 0, minimum_order: 0, applies_to: [],
};

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data || []));
  useEffect(() => { load(); api.get("/admin/products").then((r) => setProducts(r.data || [])).catch(() => {}); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c, applies_to: c.applies_to || [] }); setEditing(c.code); setOpen(true); };

  const toggleSlug = (slug) => {
    const has = form.applies_to.includes(slug);
    setForm({ ...form, applies_to: has ? form.applies_to.filter((s) => s !== slug) : [...form.applies_to, slug] });
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      discount_value: Number(form.discount_value || 0),
      usage_limit: Number(form.usage_limit || 0),
      minimum_order: Number(form.minimum_order || 0),
    };
    try {
      if (editing) await api.patch(`/admin/coupons/${editing}`, body);
      else await api.post("/admin/coupons", body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    await api.delete(`/admin/coupons/${code}`); load();
  };

  return (
    <div data-testid="admin-coupons-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-2">Marketing</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Coupons</h1>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md" data-testid="admin-new-coupon">
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> New coupon
        </Button>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No coupons yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Code</th>
                <th className="text-left px-5 py-3">Description</th>
                <th className="text-right px-5 py-3">Discount</th>
                <th className="text-right px-5 py-3">Min order</th>
                <th className="text-right px-5 py-3">Usage</th>
                <th className="text-left px-5 py-3">Applies to</th>
                <th className="text-left px-5 py-3">Expires</th>
                <th className="text-left px-5 py-3">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.code} className="border-b border-slate-100 last:border-b-0" data-testid={`coupon-${c.code}`}>
                <td className="px-5 py-3 font-mono text-slate-900">
                  {c.code}
                  {c.is_affiliate_coupon && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[9px] uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200" data-testid={`aff-badge-${c.code}`}>Affiliate</span>}
                </td>
                  <td className="px-5 py-3 text-slate-600 max-w-xs truncate">{c.description}</td>
                  <td className="px-5 py-3 text-right font-mono">{c.discount_type === "percent" ? `${c.discount_value}%` : `$${c.discount_value}`}</td>
                  <td className="px-5 py-3 text-right font-mono">{c.minimum_order ? `$${Number(c.minimum_order).toFixed(2)}` : "—"}</td>
                  <td className="px-5 py-3 text-right font-mono text-xs">
                    {c.used_count || 0}
                    {c.usage_limit ? ` / ${c.usage_limit}` : ""}
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {(c.applies_to || []).length === 0 ? <span className="text-slate-400">All products</span> : (c.applies_to || []).slice(0, 2).join(", ") + ((c.applies_to || []).length > 2 ? ` +${(c.applies_to || []).length - 2}` : "")}
                  </td>
                  <td className="px-5 py-3 text-slate-600 text-xs font-mono">{c.expires_at ? c.expires_at.slice(0, 10) : "—"}</td>
                  <td className="px-5 py-3">
                    <Badge className={`rounded-md border font-mono text-[10px] uppercase tracking-widest ${c.active ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {c.active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(c)} className="p-1.5 rounded-md hover:bg-slate-100" data-testid={`coupon-edit-${c.code}`}><Pencil className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.75}/></button>
                    <button onClick={() => del(c.code)} className="p-1.5 rounded-md hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-500" strokeWidth={1.75}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? `Edit ${editing}` : "New coupon"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input required placeholder="Code (e.g. LAB20)" value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} disabled={!!editing} data-testid="coupon-code"/>
            <Textarea rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Discount type</label>
                <select value={form.discount_type} onChange={(e) => setForm({...form, discount_type: e.target.value})} className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 text-sm" data-testid="coupon-discount-type">
                  <option value="percent">Percent (%)</option><option value="fixed">Fixed ($)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Amount</label>
                <Input type="number" step="0.01" min="0" required value={form.discount_value} onChange={(e) => setForm({...form, discount_value: e.target.value})} className="mt-1" data-testid="coupon-discount-value"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Minimum order ($)</label>
                <Input type="number" step="0.01" min="0" value={form.minimum_order} onChange={(e) => setForm({...form, minimum_order: e.target.value})} className="mt-1" placeholder="0 = no minimum"/>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Usage limit</label>
                <Input type="number" min="0" value={form.usage_limit} onChange={(e) => setForm({...form, usage_limit: e.target.value})} className="mt-1" placeholder="0 = unlimited"/>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Expires (YYYY-MM-DD)</label>
              <Input placeholder="Optional" value={form.expires_at || ""} onChange={(e) => setForm({...form, expires_at: e.target.value})} className="mt-1"/>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Applies to</label>
              <div className="mt-1 max-h-48 overflow-y-auto border border-slate-200 rounded-md p-2">
                <label className="flex items-center gap-2 text-xs px-2 py-1 hover:bg-slate-50 rounded">
                  <input type="checkbox" checked={form.applies_to.length === 0} onChange={() => setForm({...form, applies_to: []})}/> All products
                </label>
                {products.map((p) => (
                  <label key={p.slug} className="flex items-center gap-2 text-xs px-2 py-1 hover:bg-slate-50 rounded">
                    <input type="checkbox" checked={form.applies_to.includes(p.slug)} onChange={() => toggleSlug(p.slug)} data-testid={`coupon-slug-${p.slug}`}/>
                    <span className="font-mono text-slate-500">{p.slug}</span>
                    <span className="text-slate-700">· {p.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})}/> Active</label>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="coupon-save" className="bg-blue-600 hover:bg-blue-700 text-white">{editing ? "Save" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
